import { useEffect, useState } from "react";

const LOCAL_STORAGE_CHANGE_EVENT = "local-storage-change";

const getStoredValue = <T,>(key: string, initialValue: T | null): T | null => {
  const item = localStorage.getItem(key);
  if (!item) {
    return initialValue;
  }

  try {
    return JSON.parse(item);
  } catch {
    return item as unknown as T;
  }
};

const useLocalStorage = <T,>(
  key: string,
  initialValue: T | null = null,
  // eslint-disable-next-line
): [T | null, (value: T | null) => void] => {
  const [storedValue, setStoredValue] = useState<T | null>(() => getStoredValue(key, initialValue));

  useEffect(() => {
    const syncStoredValue = () => {
      setStoredValue(getStoredValue(key, initialValue));
    };

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key) {
        syncStoredValue();
      }
    };

    const handleLocalStorageChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ key?: string }>;
      if (customEvent.detail?.key === key) {
        syncStoredValue();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener(LOCAL_STORAGE_CHANGE_EVENT, handleLocalStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(LOCAL_STORAGE_CHANGE_EVENT, handleLocalStorageChange);
    };
  }, [initialValue, key]);

  const setValue = (value: T | null): void => {
    setStoredValue(value);
    if (value !== null) {
      if (typeof value === "string") {
        localStorage.setItem(key, value);
      } else {
        localStorage.setItem(key, JSON.stringify(value));
      }
    } else {
      localStorage.removeItem(key);
    }

    window.dispatchEvent(
      new CustomEvent(LOCAL_STORAGE_CHANGE_EVENT, {
        detail: { key },
      }),
    );
  };

  return [storedValue, setValue];
};

export default useLocalStorage;
